import axiosInstance from './axiosConfig';
import { ADMIN_API } from '../constants/api';

const permissionService = {
  // Lấy danh sách quyền của user
  getUserPermissions: async (userId) => {
    try {
      // Thử lấy từ endpoint permissions trước
      try {
        const response = await axiosInstance.get(`${ADMIN_API.DETAIL(userId)}/permissions`);
        if (response.data && response.data.permissions) {
          return response.data.permissions;
        }
      } catch (permError) {
        // Nếu endpoint permissions không tồn tại, thử lấy từ user detail
        console.log('Endpoint permissions không tồn tại, thử lấy từ user detail');
      }
      
      // Lấy từ user detail endpoint
      const userResponse = await axiosInstance.get(ADMIN_API.DETAIL(userId));
      if (userResponse.data && userResponse.data.permissions) {
        // Nếu permissions là string, parse thành array
        let permissions = userResponse.data.permissions;
        if (typeof permissions === 'string') {
          try {
            permissions = JSON.parse(permissions);
          } catch (e) {
            // Nếu không parse được, coi như là string đơn giản
            permissions = [permissions];
          }
        }
        return Array.isArray(permissions) ? permissions : [];
      }
      
      return [];
    } catch (error) {
      console.error('Lỗi khi lấy quyền của user:', error);
      return [];
    }
  },

  // Cập nhật quyền của user
  updateUserPermissions: async (userId, permissions) => {
    try {
      const response = await axiosInstance.put(`${ADMIN_API.DETAIL(userId)}/permissions`, {
        permissions
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Cập nhật quyền thất bại' };
    }
  },

  // Load permissions vào user object nếu chưa có
  loadUserPermissions: async function() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.id && !user.UserID) {
      return;
    }
    
    // Nếu đã có permissions, không cần load lại
    if (user.permissions && Array.isArray(user.permissions) && user.permissions.length > 0) {
      return;
    }
    
    try {
      const userId = user.id || user.UserID;
      const permissions = await this.getUserPermissions(userId);
      if (permissions && permissions.length > 0) {
        // Cập nhật user object trong localStorage
        const updatedUser = { ...user, permissions };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Lỗi khi load permissions:', error);
    }
  },

  // Kiểm tra quyền của user hiện tại
  checkPermission: (permissionKey) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Nếu user không có permissions, return false
    if (!user.permissions) {
      return false;
    }
    
    // Chuyển đổi permissions thành array nếu là string
    let permissions = user.permissions;
    if (typeof permissions === 'string') {
      try {
        permissions = JSON.parse(permissions);
      } catch (e) {
        // Nếu không parse được, coi như là string đơn giản
        permissions = [permissions];
      }
    }
    
    // Đảm bảo permissions là array
    if (!Array.isArray(permissions)) {
      permissions = [permissions];
    }
    
    // Kiểm tra nếu user có full quyền (chứa "*" hoặc "all")
    if (permissions.includes('*') || permissions.includes('all')) {
      return true;
    }
    
    // Kiểm tra permission cụ thể
    return permissions.includes(permissionKey) || false;
  },

  // Lấy danh sách tất cả quyền có trong hệ thống (theo thứ tự trong database)
  getAllPermissions: () => {
    return [
      // Quyền tổng quan
      { key: 'view_dashboard', label: 'Xem tổng quan' },
      
      // Quyền quản lý nhà xưởng
      { key: 'view_factories', label: 'Xem danh sách nhà xưởng' },
      { key: 'add_factories', label: 'Thêm nhà xưởng' },
      { key: 'edit_factories', label: 'Sửa nhà xưởng' },
      { key: 'delete_factories', label: 'Xóa nhà xưởng' },
      
      // Quyền quản lý khách thuê
      { key: 'view_tenants', label: 'Xem danh sách khách thuê' },
      { key: 'add_tenants', label: 'Thêm khách thuê' },
      { key: 'edit_tenants', label: 'Sửa khách thuê' },
      { key: 'delete_tenants', label: 'Xóa khách thuê' },
      
      // Quyền quản lý hợp đồng
      { key: 'view_contracts', label: 'Xem danh sách hợp đồng' },
      { key: 'add_contracts', label: 'Thêm hợp đồng' },
      { key: 'edit_contracts', label: 'Sửa hợp đồng' },
      { key: 'delete_contracts', label: 'Xóa hợp đồng' },
      
      // Quyền quản lý hóa đơn thuê (theo thứ tự database: add, view, edit, delete, pay, remind)
      { key: 'add_rent_invoices', label: 'Thêm hóa đơn thuê' },
      { key: 'view_rent_invoices', label: 'Xem hóa đơn thuê' },
      { key: 'edit_rent_invoices', label: 'Sửa hóa đơn thuê' },
      { key: 'delete_rent_invoices', label: 'Xóa hóa đơn thuê' },
      { key: 'pay_rent_invoices', label: 'Thanh toán hóa đơn thuê' },
      { key: 'remind_rent_invoices', label: 'Gửi nhắc nhở hóa đơn thuê' },
      
      // Quyền quản lý điện (theo thứ tự database: view, edit, delete, add, pay, remind)
      { key: 'view_electricity', label: 'Xem thông tin điện' },
      { key: 'edit_electricity', label: 'Sửa thông tin điện' },
      { key: 'delete_electricity', label: 'Xóa thông tin điện' },
      { key: 'add_electricity', label: 'Thêm thông tin điện' },
      { key: 'pay_electricity', label: 'Thanh toán tiền điện' },
      { key: 'remind_electricity', label: 'Gửi nhắc nhở tiền điện' },
      
      // Quyền quản lý nước
      { key: 'view_water', label: 'Xem thông tin nước' },
      { key: 'add_water', label: 'Thêm thông tin nước' },
      { key: 'edit_water', label: 'Sửa thông tin nước' },
      { key: 'delete_water', label: 'Xóa thông tin nước' },
      { key: 'pay_water', label: 'Thanh toán tiền nước' },
      { key: 'remind_water', label: 'Gửi nhắc nhở tiền nước' },
      
      // Quyền quản lý nước thải
      { key: 'view_waste_water', label: 'Xem thông tin nước thải' },
      { key: 'add_waste_water', label: 'Thêm thông tin nước thải' },
      { key: 'edit_waste_water', label: 'Sửa thông tin nước thải' },
      { key: 'delete_waste_water', label: 'Xóa thông tin nước thải' },
      { key: 'pay_waste_water', label: 'Thanh toán tiền nước thải' },
      { key: 'remind_waste_water', label: 'Gửi nhắc nhở tiền nước thải' },

      
      // Quyền quản lý hỗ trợ
      { key: 'view_supports', label: 'Xem danh sách hỗ trợ' },
      { key: 'add_supports', label: 'Thêm hỗ trợ' },
      { key: 'edit_supports', label: 'Sửa hỗ trợ' },
      { key: 'delete_supports', label: 'Xóa hỗ trợ' },
      
      // Quyền quản lý thông báo
      { key: 'view_notifications', label: 'Xem thông báo' },
      { key: 'add_notifications', label: 'Thêm thông báo' },
      { key: 'edit_notifications', label: 'Sửa thông báo' },
      { key: 'delete_notifications', label: 'Xóa thông báo' },
      
      // Quyền xem báo cáo
      { key: 'view_reports', label: 'Xem báo cáo' },
      
      // Quyền quản lý hệ thống
      { key: 'view_admins', label: 'Xem danh sách tài khoản' },
      { key: 'add_admins', label: 'Thêm tài khoản' },
      { key: 'edit_admins', label: 'Sửa tài khoản' },
      { key: 'delete_admins', label: 'Xóa tài khoản' },
      { key: 'manage_permissions', label: 'Quản lý phân quyền' },
      { key: 'view_user_activities', label: 'Xem lịch sử hoạt động' },
      { key: 'view_utilities', label: 'Quản lý tiện ích' },
      { key: 'view_services', label: 'Quản lý dịch vụ' },
    ];
  }
};

export default permissionService;